import { logger } from "$lib/utils/logger";
import { ensureError } from "./errorReporter";

interface HealthMonitorContext {
  consecutiveFailures: number;
  failureStreakMs: number;
}

interface HealthMonitorOptions {
  name: string;
  unhealthyAfterMs: number;
  onUnhealthy: (lastError: Error, ctx: HealthMonitorContext) => void;
  onRecovered?: (ctx: HealthMonitorContext) => void;
}

interface HealthMonitor {
  recordSuccess: () => void;
  recordFailure: (error: unknown) => void;
}

function createHealthMonitor(options: HealthMonitorOptions): HealthMonitor {
  const { name, unhealthyAfterMs, onUnhealthy, onRecovered } = options;
  let firstFailureInStreakAt: number | null = null;
  let consecutiveFailures = 0;
  let isUnhealthy = false;

  function recordSuccess() {
    const wasUnhealthy = isUnhealthy;
    const failureStreakMs =
      firstFailureInStreakAt === null
        ? 0
        : performance.now() - firstFailureInStreakAt;
    firstFailureInStreakAt = null;
    consecutiveFailures = 0;
    isUnhealthy = false;
    if (wasUnhealthy) {
      logger.info(
        `[HealthMonitor:${name}] Recovered after ${failureStreakMs}ms`
      );
      onRecovered?.({ consecutiveFailures: 0, failureStreakMs });
    }
  }

  function recordFailure(error: unknown) {
    if (firstFailureInStreakAt === null) {
      firstFailureInStreakAt = performance.now();
    }
    consecutiveFailures++;
    const errorObj = ensureError(error);
    const failureStreakMs = performance.now() - firstFailureInStreakAt;
    if (!isUnhealthy && failureStreakMs >= unhealthyAfterMs) {
      isUnhealthy = true;
      logger.warn(
        `[HealthMonitor:${name}] Unhealthy after ${failureStreakMs}ms / ${consecutiveFailures} consecutive failures`
      );
      onUnhealthy(errorObj, { consecutiveFailures, failureStreakMs });
    }
  }

  return { recordSuccess, recordFailure };
}

export { createHealthMonitor };
export type { HealthMonitorOptions, HealthMonitorContext, HealthMonitor };
